import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Badge,
  Spinner,
  CardHeader,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  Edit3,
  Calendar,
  ExternalLink,
  Tag,
  ImageIcon,
  FileText,
  AlignLeft,
} from "lucide-react";
import NewsService from "../../../services/KangarooServices/NewsService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { formatDateTime } from "../../../Components/Common/FormatDate";
import { imageBaseApi } from "../../../utilis/imageHelper";

const NewsDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDatabyId = async (id) => {
    try {
      setLoading(true);
      const res = await NewsService.getDetail(id);
      setData(res);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatabyId(id);
  }, [id]);

  const breadcrumbItems = [{ title: "Back to List", link: "/news" }];

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center min-vh-50">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="News Detail"
            pageTitle="News"
            breadcrumbItems={breadcrumbItems}
          />

          {/* Header Section */}
          <Row>
            <Col lg={12}>
              <Card className="border-0 shadow-sm mb-4">
                <div className="bg-primary-subtle">
                  <CardBody className="p-4">
                    <Row className="align-items-center">
                      <Col md={10}>
                        <div className="d-flex align-items-start gap-3 mb-3">
                          <Badge
                            color={data?.isActive ? "success" : "secondary"}
                            className="px-3 py-2 fs-6"
                          >
                            {data?.isActive ? "Active" : "Inactive"}
                          </Badge>
                          {data?.category && (
                            <Badge color="info" className="px-3 py-2 fs-6">
                              <Tag size={14} className="me-1" />
                              {data.category}
                            </Badge>
                          )}
                        </div>
                        <h2 className="fw-bold mb-3 lh-base">{data?.title}</h2>
                        <div className="d-flex flex-wrap gap-3 text-muted">
                          {data?.publishedAt && (
                            <div className="d-flex align-items-center gap-2">
                              <Calendar size={18} />
                              <span className="small">
                                Published: {formatDateTime(data.publishedAt)}
                              </span>
                            </div>
                          )}
                          {data?.createdAt && !data?.publishedAt && (
                            <div className="d-flex align-items-center gap-2">
                              <Calendar size={18} />
                              <span className="small">
                                Created: {formatDateTime(data.createdAt)}
                              </span>
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col md={2} className="text-end">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm d-flex align-items-center gap-2 ms-auto"
                          onClick={() => navigate(`/news/update/${id}`)}
                        >
                          <Edit3 size={14} />
                          Update
                        </button>
                      </Col>
                    </Row>
                  </CardBody>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Main Content */}
          <Row>
            <Col lg={8}>
              {/* Full Text Section */}
              {data?.fullText && (
                <Card className="border-0 shadow-sm mb-4">
                  <CardHeader className="bg-transparent border-bottom py-3">
                    <h5 className="mb-0 d-flex align-items-center gap-2 text-dark">
                      <FileText size={18} className="text-primary" />
                      News Content
                    </h5>
                  </CardHeader>
                  <CardBody>
                    
                    <p className=" border-4 border-start border-primary rounded-start ps-2 fs-6 lh-lg text-dark">{data.fullText}</p>
                  </CardBody>
                </Card>
              )}

              {/* Short Description Section */}
              {data?.shortDescription && (
                <Card className="border-0 shadow-sm mb-4">
                  <CardHeader className="bg-transparent border-bottom py-3">
                    <h5 className="mb-0 d-flex align-items-center gap-2 text-dark">
                      <AlignLeft size={18} className="text-primary" />
                      News Content
                    </h5>
                  </CardHeader>
                  <CardBody>
                    
                    <div
                      className="fs-6 lh-lg text-secondary"
                      dangerouslySetInnerHTML={{
                        __html: data.shortDescription,
                      }}
                    />
                  </CardBody>
                </Card>
              )}
            </Col>

            {/* Sidebar - Featured Image */}
            <Col lg={4}>
              {data?.featuredMedia?.url && (
                <Card className="border-0 shadow-sm mb-4 overflow-hidden">
                  <CardHeader className="bg-transparent border-bottom py-3">
                    <h5 className="mb-0 d-flex align-items-center gap-2 text-dark">
                      <ImageIcon size={18} className="text-primary" />
                      Featured Image
                    </h5>
                  </CardHeader>
                  <CardBody className="p-0">
                    <div className="bg-light d-flex justify-content-center">
                      <img
                        src={`${imageBaseApi}${data?.featuredMedia?.url}`}
                        alt={data.featuredMedia.fileName}
                        className="img-fluid w-100"
                        style={{ maxHeight: "250px", objectFit: "contain" }}
                      />
                    </div>
                    <div className="p-3 bg-white">
                      <div className="d-flex align-items-center gap-2 text-muted small">
                        <Tag size={12} className="text-primary" />
                        <span className="text-truncate">
                          {data.featuredMedia.fileName}
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Quick Info Card
              <Card className="border-0 shadow-sm bg-light">
                <CardBody className="p-4">
                  <h6 className="fw-bold mb-3">Quick Information</h6>
                  <div className="d-flex flex-column gap-3">
                    <div>
                      <div className="text-muted small mb-1">Status</div>
                      <Badge 
                        color={data?.isActive ? "success" : "secondary"}
                        className="px-3 py-2"
                      >
                        {data?.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    {data?.category && (
                      <div>
                        <div className="text-muted small mb-1">Category</div>
                        <div className="fw-medium">{data.category}</div>
                      </div>
                    )}
                    {data?.publishedAt ? (
                      <div>
                        <div className="text-muted small mb-1">Published</div>
                        <div className="fw-medium small">
                          {formatDateTime(data.publishedAt)}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-muted small mb-1">Created</div>
                        <div className="fw-medium small">
                          {formatDateTime(data.createdAt)}
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card> */}

              {/* Additional Information */}
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <h5 className="fw-bold text-uppercase mb-4 text-primary">
                    Additional Information
                  </h5>

                  <Row className="g-3">
                    {data?.publishedAt && (
                      <Col xs={12}>
                        <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                          <span className="text-muted fw-medium">
                            Published Date
                          </span>
                          <span className="fw-semibold">
                            {formatDateTime(data.publishedAt)}
                          </span>
                        </div>
                      </Col>
                    )}

                    <Col xs={12}>
                      <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                        <span className="text-muted fw-medium">Status</span>
                        <Badge
                          color={data?.isActive ? "success" : "secondary"}
                          className="px-3 py-2 fs-6"
                        >
                          {data?.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </Col>

                    {data?.category && (
                      <Col xs={12}>
                        <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                          <span className="text-muted fw-medium">Category</span>
                          <Badge color="info" className="px-3 py-2 fs-6">
                            {data.category}
                          </Badge>
                        </div>
                      </Col>
                    )}

                    {data?.sourceLink && (
                      <Col xs={12}>
                        <div className="pb-3">
                          <p className="text-muted fw-medium mb-2">
                            Source Link
                          </p>
                          <a
                            href={
                              data.sourceLink.startsWith("http")
                                ? data.sourceLink
                                : `https://${data.sourceLink}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none d-flex align-items-center gap-2"
                          >
                            <ExternalLink size={16} />
                            <span className="text-break">
                              {data.sourceLink}
                            </span>
                          </a>
                        </div>
                      </Col>
                    )}

                    {data?.createdAt && (
                      <Col xs={12}>
                        <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                          <span className="text-muted fw-medium">
                            Created At
                          </span>
                          <span className="fw-semibold small">
                            {formatDateTime(data.createdAt)}
                          </span>
                        </div>
                      </Col>
                    )}

                    {data?.updatedAt && (
                      <Col xs={12}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted fw-medium">
                            Last Updated
                          </span>
                          <span className="fw-semibold small">
                            {formatDateTime(data.updatedAt)}
                          </span>
                        </div>
                      </Col>
                    )}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default NewsDetail;
